/**
 * Экранная клавиатура и видимая область (CLIENT_WEB.md §3.4 п. 5,
 * CLIENT_IOS_WEBAPP.md §3.2).
 *
 * iOS не уменьшает layout-viewport при появлении клавиатуры: `100dvh`
 * остаётся прежней, а видимая область сокращается. Единственный переносимый
 * источник правды — `visualViewport`. Здесь считаются три величины:
 *
 *  - `inset` — насколько клавиатура сократила видимую область
 *    (`innerHeight − visualViewport.height`). Это признак «клавиатура открыта»
 *    и мера того, сколько высоты приложение потеряло. Смещение `offsetTop`
 *    в него НЕ входит: Safari, «доворачивая» страницу к полю, уменьшает
 *    остаток layout-viewport, и с ним перекрытие падало ниже порога —
 *    клавиатура переставала распознаваться, раскладка не сжималась, и экран
 *    входа уезжал целиком (найдено на iPhone 18.09.2026).
 *  - `height` — высота видимой области: к ней приложение сжимает раскладку
 *    (`--viewport-height`, app/model/viewport.ts), чтобы шапки остались на месте.
 *  - `offsetTop` — на сколько Safari сдвинул видимое окно вниз по странице;
 *    на столько же опускается корень приложения, иначе шапка окажется выше окна.
 *  - `bottomOverlap` — часть layout-viewport ниже видимого окна: на неё
 *    поднимаются слои с `position: fixed; bottom` (листы, окна, тосты).
 */

export interface ViewportMetrics {
  /** Высота layout-viewport — `window.innerHeight`. */
  innerHeight: number
  /**
   * Наибольшая высота layout-viewport без клавиатуры (та же ориентация).
   * Android с `interactive-widget=resizes-content` сжимает сам layout-viewport,
   * и перекрытие видимой области там нулевое — клавиатура видна только по этой
   * разнице. По умолчанию равна `innerHeight`, то есть признака сжатия нет.
   */
  baseHeight?: number
  /** `visualViewport.height`. */
  height: number
  /** `visualViewport.offsetTop` — насколько iOS «довернул» видимую область. */
  offsetTop: number
  /** `visualViewport.scale` — при pinch-zoom видимая область меньше не из-за клавиатуры. */
  scale: number
}

/** Состояние видимой области для раскладки. */
export interface KeyboardState {
  /** Потерянная под клавиатурой высота, px; 0 — клавиатуры нет. */
  inset: number
  /** Высота видимой области, px. */
  height: number
  /** Сдвиг видимого окна вниз по странице, px. */
  offsetTop: number
  /** Часть layout-viewport ниже видимого окна, px. */
  bottomOverlap: number
}

/** §3.2 п. 2: перекрытие меньше порога — панели браузера, а не клавиатура. */
export const KEYBOARD_THRESHOLD_PX = 80

/** Изменение `scale` в пределах погрешности округления считается его отсутствием. */
const SCALE_EPSILON = 0.01

/**
 * Чистый расчёт: сколько высоты забрала клавиатура, или 0, если её нет.
 *
 * `editableFocused` — фокус в поле ввода: без него уменьшение видимой области
 * клавиатурой быть не может, а вот сворачивание панелей Safari — может.
 */
export function computeKeyboardInset(metrics: ViewportMetrics, editableFocused: boolean): number {
  if (!editableFocused) return 0
  if (Math.abs(metrics.scale - 1) > SCALE_EPSILON) return 0

  // iOS: видимая область меньше layout-viewport. Android: сжат сам layout-viewport.
  const overlap = Math.round(metrics.innerHeight - metrics.height)
  const shrink = Math.round((metrics.baseHeight ?? metrics.innerHeight) - metrics.innerHeight)
  const inset = Math.max(overlap, shrink)
  return inset > KEYBOARD_THRESHOLD_PX ? inset : 0
}

/** Полное состояние видимой области; при закрытой клавиатуре — нули и полная высота. */
export function computeKeyboardState(metrics: ViewportMetrics, editableFocused: boolean): KeyboardState {
  const inset = computeKeyboardInset(metrics, editableFocused)
  if (inset === 0) {
    return { inset: 0, height: Math.round(metrics.height), offsetTop: 0, bottomOverlap: 0 }
  }

  return {
    inset,
    height: Math.round(metrics.height),
    offsetTop: Math.max(0, Math.round(metrics.offsetTop)),
    bottomOverlap: Math.max(0, Math.round(metrics.innerHeight - metrics.height - metrics.offsetTop)),
  }
}

/** Поле, фокус в котором поднимает экранную клавиатуру. */
export function isEditableElement(element: Element | null): boolean {
  if (!element) return false
  if (element instanceof HTMLTextAreaElement) return true
  if (element instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'range', 'file', 'submit', 'reset', 'color'].includes(
      element.type,
    )
  }
  return element instanceof HTMLElement && element.isContentEditable === true
}

/**
 * Подписка на изменения видимой области. `onChange` вызывается только когда
 * что-то из состояния изменилось (§3.2 п. 6: повторные resize с тем же
 * значением перерисовку не вызывают).
 */
export function watchKeyboardInset(onChange: (state: KeyboardState) => void): () => void {
  const viewport = globalThis.visualViewport
  if (!viewport) return () => undefined

  let last: KeyboardState = { inset: 0, height: 0, offsetTop: 0, bottomOverlap: 0 }
  let frame: number | null = null
  /*
   * Высота окна без клавиатуры и ширина, при которой она измерена (поворот её
   * сбрасывает). Замер делается сразу: подписка начинается при старте приложения,
   * когда клавиатуры заведомо нет. Иначе первое же касание поля приходило с уже
   * сжатым окном, полной высоты никто не помнил, и клавиатура на Android
   * оставалась незамеченной до первого её закрытия.
   */
  let baseWidth = globalThis.innerWidth
  let baseHeight = globalThis.innerHeight

  /*
   * Документ не прокручивается никогда (html/body `overflow: hidden`), но Safari
   * при фокусе прокручивает его программно к полю — и по вертикали, и по горизонтали,
   * если поле в этот момент за краем (экран ещё въезжает справа). Такой сдвиг никто
   * не снимает, и экран остаётся смещённым, пока окно не изменится. Возвращаем окно
   * к нулю на каждое событие прокрутки и видимой области.
   */
  const resetDocumentScroll = (): void => {
    if (globalThis.scrollX === 0 && globalThis.scrollY === 0) return
    if (frame !== null) cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      frame = null
      globalThis.scrollTo(0, 0)
    })
  }

  const update = (): void => {
    /*
     * Фокус в поле нужен, чтобы ВОЙТИ в состояние клавиатуры (сворачивание панелей
     * Safari тоже уменьшает видимую область). Выход — только когда область снова
     * выросла: после blur клавиатура ещё четверть секунды уезжает, и если разжать
     * раскладку сразу, содержимое прыгает под ещё видимую клавиатуру.
     */
    const editableFocused = last.inset > 0 || isEditableElement(document.activeElement)

    if (baseWidth !== globalThis.innerWidth) {
      baseWidth = globalThis.innerWidth
      baseHeight = 0
    }
    // Пока поля в фокусе нет, окно заведомо без клавиатуры — это и есть его полная высота
    if (!editableFocused) baseHeight = Math.max(baseHeight, globalThis.innerHeight)

    const state = computeKeyboardState(
      {
        innerHeight: globalThis.innerHeight,
        baseHeight: baseHeight || globalThis.innerHeight,
        height: viewport.height,
        offsetTop: viewport.offsetTop,
        scale: viewport.scale,
      },
      editableFocused,
    )

    if (state.inset > 0) resetDocumentScroll()

    const same =
      state.inset === last.inset &&
      state.height === last.height &&
      state.offsetTop === last.offsetTop &&
      state.bottomOverlap === last.bottomOverlap
    if (same) return

    last = state
    onChange(state)
  }

  viewport.addEventListener('resize', update)
  viewport.addEventListener('scroll', update)
  // Android сжимает layout-viewport: событие приходит окну, а не visualViewport
  globalThis.addEventListener('resize', update)
  // Фокус меняется раньше, чем приходит resize: пересчёт по обоим событиям.
  document.addEventListener('focusin', update)
  document.addEventListener('focusout', update)
  globalThis.addEventListener('scroll', resetDocumentScroll)

  return () => {
    viewport.removeEventListener('resize', update)
    viewport.removeEventListener('scroll', update)
    globalThis.removeEventListener('resize', update)
    document.removeEventListener('focusin', update)
    document.removeEventListener('focusout', update)
    globalThis.removeEventListener('scroll', resetDocumentScroll)
    if (frame !== null) cancelAnimationFrame(frame)
    if (last.inset !== 0) {
      onChange({ inset: 0, height: Math.round(viewport.height), offsetTop: 0, bottomOverlap: 0 })
    }
  }
}
